import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { loc } from "../lib/localize";
import { sideColor } from "../lib/odds";
import { Button } from "../components/ui/button";
import { ArrowRight, BookOpen, Coins, Scale, TrendingUp } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/12198534/pexels-photo-12198534.jpeg";
const BOARD_IMG = "https://images.unsplash.com/photo-1577401159468-3bbc7ee440b5";

export default function LandingPage() {
  const { t, lang } = useLanguage();
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api.get("/articles").then(({ data }) => setArticles(data.slice(0, 4))).catch(() => {});
  }, []);

  const cards = [
    { icon: BookOpen, title: t("landing.card1Title"), text: t("landing.card1Text") },
    { icon: Scale, title: t("landing.card2Title"), text: t("landing.card2Text") },
    { icon: Coins, title: t("landing.card3Title"), text: t("landing.card3Text") },
  ];

  return (
    <main className="relative z-10">
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-36">
          <div className="max-w-3xl animate-rise">
            <div className="mb-6 inline-flex items-center gap-2 border border-primary/40 bg-primary/10 px-3 py-1.5 font-mono text-xs uppercase tracking-widest text-primary">
              <span className="h-2 w-2 animate-pulse bg-primary" /> {t("landing.badge")}
            </div>
            <h1 className="font-heading text-4xl font-black leading-none tracking-tighter sm:text-6xl lg:text-7xl">
              {t("landing.h1a")}<span className="text-primary">{t("landing.h1accent")}</span>?
            </h1>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-foreground/80 md:text-xl">
              {t("landing.intro")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Button asChild size="lg" data-testid="hero-explore-btn" className="rounded-md bg-primary text-base font-semibold uppercase tracking-wider hover:bg-[#FF645A]">
                <Link to="/wiki">{t("landing.explore")} <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" data-testid="hero-credits-btn" className="rounded-md border-white/20 bg-transparent text-base font-semibold uppercase tracking-wider hover:bg-white/10">
                <Link to="/register">{t("landing.buyCredits")}</Link>
              </Button>
            </div>
            <p data-testid="hero-bonus" className="mt-6 text-sm font-semibold text-credit">{t("landing.bonus")}</p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 md:px-10">
        <h2 className="mb-3 font-heading text-2xl font-black tracking-tight sm:text-4xl">{t("landing.howTitle")}</h2>
        <p className="mb-10 max-w-xl text-muted-foreground">{t("landing.howSub")}</p>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {cards.map((c, i) => (
            <div key={i} className="group border border-white/10 bg-card p-8 transition-transform hover:-translate-y-1 hover:border-white/30">
              <c.icon className="mb-6 h-8 w-8 text-primary" />
              <h3 className="mb-3 font-heading text-xl font-bold">{c.title}</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">{c.text}</p>
            </div>
          ))}
        </div>
      </section>

      {articles.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 pb-24 md:px-10">
          <div className="mb-10 flex items-end justify-between">
            <h2 className="font-heading text-2xl font-black tracking-tight sm:text-4xl">{t("landing.market")}</h2>
            <Link to="/wiki" className="flex items-center gap-1 text-sm uppercase tracking-widest text-primary hover:underline">
              {t("landing.all")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {articles.map((a) => (
              <Link key={a.id} to={lang === "en" ? `/en/wiki/${a.slug_en || a.slug}` : `/wiki/${a.slug}`} data-testid="featured-article" className="group flex flex-col justify-between border border-white/10 bg-card p-6 transition-transform hover:-translate-y-1 hover:border-white/30">
                <div>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{loc(a, "category", lang)}</span>
                  <h3 className="mt-2 font-heading text-lg font-bold leading-tight">{loc(a, "title", lang)}</h3>
                </div>
                <div className="mt-6 flex items-end justify-between border-t border-white/10 pt-4">
                  <span className="font-mono text-xs text-muted-foreground">{t("landing.payout")}</span>
                  <span className={`font-heading text-3xl font-black ${sideColor(a.multiplier_side)}`}>{a.headline_multiplier}×</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="border-t border-white/10 bg-card">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-16 md:flex-row md:items-center md:px-10">
          <img src={BOARD_IMG} alt="" className="h-40 w-full max-w-xs border border-white/10 object-cover md:w-48" />
          <div>
            <TrendingUp className="mb-3 h-6 w-6 text-credit" />
            <h3 className="font-heading text-xl font-bold">{t("landing.discTitle")}</h3>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">{t("landing.discText")}</p>
          </div>
        </div>
      </section>
    </main>
  );
}
